// frontend/src/pages/citizen/PreviousVisitsList.jsx
// Compact layout: Visit ID, Date/Time, Centre, Status, Return Note, Diff Dx, Approved by, Action (👁️)
// Shows true historical visits only. View opens the canonical Visit Workspace in read-only mode.
// Sticky header, responsive height, fast search across key fields.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Chip, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Skeleton, IconButton
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import http from '../../services/http';

// -------- tenant-safe navigation ----------
function tenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}
function withTenantRoute(path) {
  const base = tenantBase();
  const p = String(path || '');
  const clean = p.startsWith('/') ? p : `/${p}`;
  return `${base}${clean}`.replace(/\/{2,}/g, '/');
}

// -------- helpers to accept various API shapes ----------
function normalizeVisits(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.visits)) return payload.visits;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.visits?.items)) return payload.visits.items;
  return [];
}
const ts = (v) => new Date(v?.visitDate || v?.updatedAt || v?.createdAt || 0).getTime();
const sortDescByTime = (list) => [...list].sort((a, b) => ts(b) - ts(a));

const statusLabel = (s = '') => {
  const t = String(s).toLowerCase();
  if (['pending', 'submitted', 'awaiting review'].includes(t)) return 'Awaiting review';
  if (t === 'approved') return 'Approved';
  if (t === 'returned') return 'Returned to edit';
  if (t === 'draft') return 'Draft';
  return t || '-';
};
const chipColor = (rawStatus = '') => {
  const t = String(rawStatus).toLowerCase();
  if (['pending', 'submitted', 'awaiting review'].includes(t)) return 'warning';
  if (t === 'approved') return 'success';
  if (t === 'returned') return 'default';
  if (t === 'draft') return 'default';
  return 'default';
};

function getApprover(v) {
  const rx = v?.prescription || {};
  if (rx?.approvedBy && (rx.approvedBy.name || rx.approvedBy.role || rx.approvedBy.at)) return rx.approvedBy;
  if (v?.approvedBy && (v.approvedBy.name || v.approvedBy.role || v.approvedBy.at)) return v.approvedBy;
  const name = rx.approvedByName || v.approvedByName || v.approverName || rx.approverName || rx.approvedByUser || v.approvedByUser || '';
  const role = rx.approvedByRole || v.approvedByRole || v.approverRole || '';
  const at   = rx.approvedAt || v.approvedAt || (rx.approvedBy && rx.approvedBy.at) || (v.approvedBy && v.approvedBy.at) || '';
  return (name || role || at) ? { name, role, at } : null;
}

const safe = (x) => (x == null ? '' : String(x));
const limit = (s, n = 90) => {
  const txt = safe(s).trim();
  return txt.length > n ? txt.slice(0, n - 1) + '…' : txt;
};
const sameCitizen = (raw, citizenId) => {
  const target = safe(citizenId).trim();
  if (!target) return false;
  const candidates = [
    raw?.citizenId,
    raw?.citizen?.citizenId,
    raw?.citizen?.id,
    raw?.citizen?._id,
    raw?.citizen,
    raw?.cid,
    raw?.citizenDetails?.citizenId,
    raw?.citizenDetails?.id,
    raw?.citizenRef?.citizenId,
    raw?.citizenRef?.id,
  ]
    .map((v) => {
      if (v && typeof v === 'object') return safe(v.citizenId || v.id || v._id).trim();
      return safe(v).trim();
    })
    .filter(Boolean);
  return candidates.includes(target);
};
const ddxFrom = (v) => {
  const rx = v?.prescription || {};
  return rx.differentialDx || rx.ddx || rx.provisionalDx || v?.diagnosis || '';
};
const returnNoteOf = (v) => v.returnNote || v?.prescription?.returnNote || v?.rxReturnNote || '';

export default function PreviousVisitsList() {
  const { citizenId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  // responsive container height
  const contRef = useRef(null);
  const [maxH, setMaxH] = useState(520);
  useEffect(() => {
    const recalc = () => {
      const el = contRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setMaxH(Math.max(260, window.innerHeight - top - 24));
    };
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, []);

  // load visits
  useEffect(() => {
    if (!citizenId) return;
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        const enc = encodeURIComponent(citizenId);
        const endpoints = [
          `/api/visits?citizenId=${enc}`,
          `/api/citizens/${enc}/visits`,
          `/api/visits/citizen/${enc}`,
          `/api/visit-history?citizenId=${enc}`,
        ];
        let all = [];
        for (const url of endpoints) {
          try {
            const { data } = await http.get(url, { signal: ctrl.signal });
            const arr = normalizeVisits(data);
            all = (arr || []).filter((visit) => sameCitizen(visit, citizenId));
            // Empty is a valid citizen-scoped result. Do not continue to broader fallbacks.
            break;
          } catch (e) {
            if (ctrl.signal.aborted) return;
          }
        }

        // ✅ IMPORTANT: accept _id as visit identifier too (no regression)
        const mapped = (all || []).map(v => {
          const raw = v || {};
          const vid =
            raw.visitId ||
            raw.id ||
            raw._id ||              // ✅ accept Mongo id too
            raw.visit_id ||
            raw.visitID ||
            '';

          return ({
            visitId: vid ? String(vid) : '',
            visitDate: raw.visitDate || raw.createdAt || raw.updatedAt || null,
            centre: raw.centre || raw.chc || '',
            status: raw.prescription?.status || raw.status || 'draft',
            prescription: raw.prescription || {},
            createdByName: raw.createdByName || '',
            createdByRole: raw.createdByRole || '',
            _raw: raw,
          });
        }).filter(v => v.visitId && sameCitizen(v._raw, citizenId));

        setVisits(sortDescByTime(mapped));
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [citizenId]);

  // search across key fields (not huge columns)
  const filtered = useMemo(() => {
    // Previous Visits is historical clinical history, not an alternate current-work queue.
    // Keep draft/pending/submitted/returned Visits out of this list even if browser state is stale.
    const previousOnly = visits.filter((v) => {
      const status = String(v.status || v.prescription?.status || '').toLowerCase();
      const inFlight = ['draft', 'pending', 'submitted', 'returned', 'awaiting review'].includes(status);
      if (inFlight) return false;
      return true;
    });

    const needle = q.trim().toLowerCase();
    if (!needle) return previousOnly;
    return previousOnly.filter(v => {
      const approvedBy = getApprover(v) || {};
      const createdBy  = [v.createdByName, v.createdByRole].filter(Boolean).join(' ');
      const approverStr = [approvedBy.name, approvedBy.role].filter(Boolean).join(' ');
      const hay = [
        v.visitId, v.centre, v.status, v.prescription?.status,
        ddxFrom(v._raw), returnNoteOf(v._raw), approverStr, createdBy
      ].map(safe).map(x => x.toLowerCase());
      return hay.some(s => s.includes(needle));
    });
  }, [q, visits]);

  const whenStr = (dt) => {
    try { const d = new Date(dt); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(); }
    catch { return ''; }
  };

  const openVisit = (v) => {
    if (!v?.visitId) return;

    // Historical viewing must never replace the active/current Visit in browser state.
    // The Workspace uses mode=readonly and the hook is instructed not to persist it
    // as the current Visit.
    try { localStorage.removeItem('editReturnedVisit'); } catch {}

    const qs = new URLSearchParams({
      section: 'history',
      mode: 'readonly',
      from: 'previous-visits',
    }).toString();

    navigate(
      withTenantRoute(
        `/citizen/${encodeURIComponent(citizenId)}/visit/${encodeURIComponent(v.visitId)}/workspace?${qs}`
      ),
      { replace: false, state: { from: location.pathname, readOnly: true } }
    );
  };

  const SkeletonRow = ({ idx }) => (
    <TableRow key={`sk-${idx}`}>
      <TableCell><Skeleton width={140} /></TableCell>
      <TableCell><Skeleton width={180} /></TableCell>
      <TableCell><Skeleton width={140} /></TableCell>
      <TableCell><Skeleton width={120} /></TableCell>
      <TableCell><Skeleton width={220} /></TableCell>
      <TableCell><Skeleton width={220} /></TableCell>
      <TableCell><Skeleton width={200} /></TableCell>
      <TableCell><Skeleton width={60} /></TableCell>
    </TableRow>
  );

  const muted = <span style={{ color: 'var(--mui-palette-text-disabled)' }}>—</span>;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>Previous Visits</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Citizen: <span style={{ fontFamily: 'monospace' }}>{citizenId}</span>
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          placeholder="Search by Visit ID, centre, status, author, approver, Dx, or return note"
          fullWidth
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </Paper>

      <TableContainer ref={contRef} component={Paper} sx={{ maxHeight: maxH, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Visit ID</TableCell>
              <TableCell>Date / Time</TableCell>
              <TableCell>Centre</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Return Note</TableCell>
              <TableCell>Diff. Dx</TableCell>
              <TableCell>Approved by</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow idx={i} key={i} />)}

            {!loading && filtered.map((v, i) => {
              const when = whenStr(v.visitDate) || '-';
              const approver = getApprover(v);
              const approverLabel = approver
                ? [approver.name, approver.role && `(${approver.role})`].filter(Boolean).join(' ')
                : '';
              const approverTime = approver?.at ? whenStr(approver.at) : '';

              const rawStatus = String(v.status || v.prescription?.status || '').toLowerCase();
              const statLabel = statusLabel(rawStatus);

              const ddx = ddxFrom(v._raw);
              const rnote = returnNoteOf(v._raw);

              return (
                <TableRow key={v.visitId || i} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{v.visitId}</TableCell>
                  <TableCell>{when}</TableCell>
                  <TableCell>{v.centre || '-'}</TableCell>

                  <TableCell>
                    <Chip size="small" color={chipColor(rawStatus)} label={statLabel} />
                  </TableCell>

                  <TableCell>
                    {rnote
                      ? (
                        <Tooltip title={rnote}>
                          <span style={{ display: 'inline-block', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {rnote}
                          </span>
                        </Tooltip>
                      )
                      : muted}
                  </TableCell>

                  <TableCell>{ddx ? limit(ddx, 90) : muted}</TableCell>

                  <TableCell>
                    {approver ? (
                      <Tooltip title={approverTime ? `Approved at ${approverTime}` : ''}>
                        <span>{approverLabel}</span>
                      </Tooltip>
                    ) : muted}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="View visit">
                      <span>
                        <IconButton size="small" onClick={() => openVisit(v)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}

            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography align="center" sx={{ py: 3 }} color="text.secondary">
                    No visits yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
